import { WorkbookFilterLine, WorkbookSavedFilterData } from './../../../modules/shared/models/dynamic-table/dynamic-table-filter.modal';
import { DynamicTableColumnFilter, MappedFilter } from './../../../modules/shared/models/dynamic-table/dynamic-table-column.model';
import { FormControlList } from './../../../modules/shared/constants/formcontrol-list.constants';
import { AppMainService } from './../../../app-main.service';
import { ColumnDefinition, DynamicTableData } from './../../../modules/shared/models/dynamic-table/dynamic-table-data.model';
import { DynamicTableService } from './../../../modules/shared/services/dynamic-table/dynamic-table.service';
import { Fields, SectionN } from './../../../modules/shared/models/column-settings.model';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { WorkbookDynamicChartsM } from '../models/workbook-dynamic-chart.models';
import { WorkspaceDynamicChartService } from '../services/workspace-dynamic-chart.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { SavedFilterData } from 'src/app/modules/shared/models/filter.model';
import { filterDateRangeHelper } from './../../../modules/shared/helpers/filter-date-range.helper';
import { TimeZoneService } from './../../../modules/shared/services/timezone.service';

@Component({
  selector: 'app-workbook-dynamic-charts',
  templateUrl: './workbook-dynamic-charts.component.html',
  styleUrl: './workbook-dynamic-charts.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class WorkbookDynamicChartsComponent implements OnInit, OnChanges, OnDestroy {

    protected ngUnsubscribe$: Subject<void> = new Subject<void>();

    @Input() displayDialog: boolean = false;
    @Input() workbookId: number = 0;
    @Input() allListColumns: SectionN[] = [];
    @Output() onDialogClosed = new EventEmitter();

    contextMenuItems: any[] = [];
    dynamicCharts: WorkbookDynamicChartsM[] = [];
    dynamicTableData: DynamicTableData;

    activeTabIndex: number = 0;
    selectedTabIndex: number = 0;
    displayAddCharts: boolean = false;
    selectedChartWidget: WorkbookDynamicChartsM = null;
    isChartDataLoading: boolean = true;

    constructor(
        private messageService: MessageService,
        private readonly appMainService: AppMainService,
        private confirmationService: ConfirmationService,
        private dynamicTableService: DynamicTableService,
        private workspaceDynamicChartService: WorkspaceDynamicChartService,
        private timeZoneService: TimeZoneService
    ){

    }

    ngOnInit(): void {
        this.getWorkbookCharts();
        this.contextMenuItems = [
            {
                label: 'Edit',
                icon: 'pi pi-pencil',
                command: async () => {
                    this.showEditCharts();
                }
            },
            {
                label: 'Remove',
                icon: 'pi pi-trash',
                command: async () => {
                    this.removeChart();
                }
            }
        ];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if(changes['displayDialog'] && changes['displayDialog'].currentValue){
            this.displayDialog = changes['displayDialog'].currentValue;
        }

        if(changes['allListColumns'] && changes['allListColumns'].currentValue){
            this.allListColumns = changes['allListColumns'].currentValue;
            this.setFilterColumns();
        }
    }

    ngOnDestroy(): void {
        if (this.ngUnsubscribe$) {
            this.ngUnsubscribe$.next();
            this.ngUnsubscribe$.complete();
            this.ngUnsubscribe$.unsubscribe();
        }
    }

    closeDialog(){
        this.displayDialog = false;
        this.onDialogClosed.emit();
    }

    changeTab(e) {
        let index = e.index;
        this.selectedTabIndex = index;
        this.isChartDataLoading = true;
        const selectedChartWidget = this.dynamicCharts[this.selectedTabIndex];
        this.resetFilters();
        this.getFilterData(selectedChartWidget?.id);
    }

    selectSubTab(index: number) {
        this.activeTabIndex = index;
    }

    onAddNewChart(chartDetails: WorkbookDynamicChartsM) {
        this.getWorkbookCharts();
        this.selectedTabIndex = this.dynamicCharts.findIndex((chart) => chart.id === chartDetails?.id);
        if(this.selectedTabIndex === -1) { this.selectedTabIndex = this.dynamicCharts.length; }
        this.activeTabIndex = this.selectedTabIndex;
    }

    //#region load apis

    getWorkbookCharts(){
        this.appMainService.isShowSpinner('getWorkbookCharts');
        this.workspaceDynamicChartService.getDynamicCharts(this.workbookId).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
            next: (response: WorkbookDynamicChartsM[]) => {
                this.dynamicCharts = response||[];
                const selectedChartWidget = this.dynamicCharts[this.selectedTabIndex];
                this.resetFilters();
                this.getFilterData(selectedChartWidget?.id);
                this.appMainService.isHideSpinner('getWorkbookCharts');
            },
            error: (error) => {
                this.appMainService.isHideSpinner('getWorkbookCharts');
            }
        });
    }

    //#endregion

    //#region add new charts
    onDataLoading(event: boolean) {
        this.isChartDataLoading = event;
    }

    showAddCharts(){
        this.selectedChartWidget = null;
        this.displayAddCharts = true;
    }

    showEditCharts(){
        this.selectedChartWidget = this.dynamicCharts[this.selectedTabIndex];
        this.displayAddCharts = true;
    }

    removeChart(){
        this.confirmationService.confirm({
            message: 'Are you sure you want to remove this chart?',
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            key: 'dynamicChart',
            accept: () => {
                this.appMainService.isHideSpinner('removeWorkbookCharts');
                this.workspaceDynamicChartService.deleteDynamicCharts( this.dynamicCharts[this.selectedTabIndex].id).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
                    next: (response: any) => {
                        this.appMainService.isHideSpinner('removeWorkbookCharts');
                        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Chart removed successfully' });
                        this.selectedTabIndex = 0;
                        this.getWorkbookCharts();
                    },
                    error: (error) => {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.Message||'Error removing chart' });
                        this.appMainService.isHideSpinner('removeWorkbookCharts');
                    }
                });
            },
            reject: () => {
                // Do nothing
            }
        });
    }

    closeAddCharts(){
        this.displayAddCharts = false;
    }
    //#endregion

    //#region filters
    mappedColFilter: DynamicTableColumnFilter[] = [];
    saveFilterData: SavedFilterData;
    filterInColumns: MappedFilter[];

    setFilterColumns(){
        this.filterInColumns = null;
        this.saveFilterData = {} as any;
        this.mappedColFilter = this.mapColsToFilters(this.allListColumns[0].fields);
    }

    applyFilters(event) {
        const selectedChartWidget = this.dynamicCharts[this.selectedTabIndex];
        if (!event.selectedFilter) {
            this.filterInColumns = [];
            this.saveFilterData = { "groupByFieldId": null, "filter": [] };
            this.saveFilterDataChartWise(this.saveFilterData, selectedChartWidget?.id);
            return;
        }

        this.saveFilterData = {
            "groupByFieldId": null,
            "filter": event?.selectedColumnsForSaveFilterFieldsChannelWise
        };

        this.filterInColumns = this.mapFilterDetails(event.selectedFilter);
        this.saveFilterDataChartWise(this.saveFilterData, selectedChartWidget?.id);
    }

    resetFilters() {
        this.filterInColumns = null;
        this.saveFilterData = { "groupByFieldId": null, "filter": [] };
    }

    mapFilterDetails(filterDetails: any[]): MappedFilter[] {
        if (!filterDetails) {
            return [];
        }

        return filterDetails.map(detail => {
            const dataType = this.getDataTypeByName(detail.name);
            return {
                name: detail?.name,
                dataType: dataType,
                operationType: detail?.operationType,
                values: [...(detail?.values || [])],
                operator: detail?.operator,
            };
        });
    }

    getDataTypeByName(columnName: string): string | null {
        const column = this.allListColumns[0].fields.find(col => col.columnName === columnName);
        return column ? column.formControl : null;
    }

    mapColsToFilters(workbookColumns: Fields[]): DynamicTableColumnFilter[] {
        return workbookColumns
            .filter(col => col.enabled && ![FormControlList.FileUpload, FormControlList.Picture, FormControlList.EncryptedText, FormControlList.MultiLine, FormControlList.RichText].includes(col.formControl))
            .map(col => ({
                id: col.id,
                name: col.name,
                systemGeneratedName: col.name,
                columnName: col.name,
                dragable: true,
                enabled: col.enabled,
                isRequired: false,
                displayOrder: col.displayOrder,
                isCustom: true,
                mapObject: null,
                fieldDataType: col.fieldDataType,
                formControlType: col.formControl,
                maxLengthAfterDecimal: null,
                isDateWithTime: false,
                customAttributeObjectId: null,
                operators: col.operators,
                pickListValues: [],
                isDisabled: null,
                childFields: [],
                valueList: [],
                value: '',
                groupByColumnName: col.name,
                isGroupByColumnCSV: false
            }));
    }

    saveFilterDataChartWise(selectedColumns: SavedFilterData, chartId: number) {
        if(!chartId) return;

        let filterLines: WorkbookFilterLine[] = selectedColumns.filter.map((column: any) => {
            const field = this.getColumnNameByID(column.fieldId);
            return {
                columnId: field.intId,
                operator: column.operator,
                operationType: column.operationType,
                values: [...(column?.values || [])],
            };
        });

        this.appMainService.isShowSpinner('saveFilterDataChartWise');
        let requestBody: WorkbookSavedFilterData = {
            workbookId: this.workbookId,
            chartId: chartId,
            groupByColumnId: null,
            filterLines: filterLines
        }

        this.workspaceDynamicChartService.saveFilterData(requestBody).subscribe({
            next: (response) => {
                this.appMainService.isHideSpinner('saveFilterDataChartWise');
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error!',
                    detail: 'Error occurring when saving filter data. Please try after sometime.'
                });
                this.appMainService.isHideSpinner('saveFilterDataChartWise');
            }
        });
    }

    getColumnNameByID(columnId: any): Fields | null {
        const column = this.allListColumns[0].fields.find(col => col.id === columnId);
        return column;
    }

    getColumnByIntID(columnId: number): Fields | null {
        const column = this.allListColumns[0].fields.find(col => col.intId === columnId);
        return column;
    }

    mapFilterData(data: any): any {
        return {
            groupByFieldId: data.groupByColumnId,
            groupByColumnName: null,
            filter: data.filterLines.map((filterLine: any) => {
                const column = this.getColumnByIntID(filterLine.columnId);
                return {
                    fieldId: column.id,
                    name: column?.name || null,
                    displayName: column?.name || null,
                    operator: filterLine.operator,
                    operationType: filterLine.operationType,
                    values: [...(filterLine?.values || [])],
                    isCustom: true,
                    filterValues: []
                };
            })
        }
    }

    getFilterData(chartId: number) {
        if(!chartId) return;

        this.appMainService.isShowSpinner('getFilterData');
        this.workspaceDynamicChartService.getSavedFilterData(chartId).subscribe({
            next: (data: WorkbookSavedFilterData) => {
                this.isChartDataLoading = false;
                if (data) {
                    this.saveFilterData = structuredClone(this.mapFilterData(data));
                    this.filterInColumns = data?.filterLines.map((filteredData) => {
                        const selectecdColumn: any = this.getColumnByIntID(filteredData?.columnId);
                        let field: MappedFilter = {
                            name: selectecdColumn?.name,
                            operator: filteredData?.operator,
                            operationType: filteredData?.operationType,
                            values: filteredData?.values,
                            dataType: selectecdColumn?.formControl
                        }
                        if (filteredData.operator == "On" || filteredData.operator == "NotOn") {
                            field.values = [];
                            field = this.dateOnOperatorListValue(filteredData, field);
                        } else {
                            field.values = filteredData?.values;
                        }
                        return field;
                    })
                } else {
                    this.filterInColumns = [];
                }
                this.appMainService.isHideSpinner('getFilterData');
            },
            error: (error) => {
                this.isChartDataLoading = false;
                this.filterInColumns = [];
                // this.messageService.add({
                //     severity: 'error',
                //     summary: 'Error!',
                //     detail: 'Error occurring when getting filter data. Please try after sometime.'
                // });
                this.appMainService.isHideSpinner('getFilterData');
            },
        })
    }

    public dateOnOperatorListValue(data: {
        name?: string;
        value?: string;
        operator: string;
        operationType?: string;
        values?: string[];
        isCustom?: boolean;
        fieldId?: number;
    }, field: MappedFilter) {

        const dateRange = filterDateRangeHelper(data?.values[0]);
        let dateRangeFrom: Date | null = dateRange?.dateRangeFrom;
        let dateRangeTo: Date | null = dateRange.dateRangeTo;

        if ((dateRangeFrom && dateRangeTo)) { 
            const fieldDetails = this.mappedColFilter.find(fld => fld?.columnName == data?.name);
            field.operator = data.operator == 'NotOn' ? 'NotBetween' : 'Between';
            dateRangeFrom = this.timeZoneService._getUTCDateTimeWithZone(dateRangeFrom, (fieldDetails?.isDateWithTime || false), true, !['This Month', 'Last Month'].includes(data?.values[0]));
            dateRangeTo = this.timeZoneService._getUTCDateTimeWithZone(dateRangeTo, (fieldDetails?.isDateWithTime || false), true, !['This Month', 'Last Month'].includes(data?.values[0]));
            dateRangeTo.setDate(dateRangeTo.getDate() + 1);
            dateRangeTo = new Date(dateRangeTo.getTime() - 1000);
            field.values.push(this.timeZoneService._getUTCDateTimeString(dateRangeFrom), this.timeZoneService._getUTCDateTimeString(dateRangeTo));
        } else if (data.values?.[0]) {
            field.operator = data.operator == 'NotOn' ? 'NotBetween' : 'Between';
            field.values.push(data.values?.[0], data.values?.[0])
        }

        return field;
    }
    //#endregion
}
